import {
    buildTopologySubEntityId,
    isTopologyEdgeCoordinateRef,
} from '../../../../../topology/TopologyReferenceBuilders';

/**
 * 边相邻面提示。
 */
export interface BlendEdgeTopologyFaceHint {
    /** 面持久化引用。 */
    persistentRef: string;
    /** 面拓扑索引。 */
    topologyIndex: number;
    /** 面形体哈希。 */
    shapeHash?: number;
}

/**
 * 边运行时拓扑提示。
 */
export interface BlendEdgeTopologyRuntimeHint {
    /** 提示类型。 */
    kind: 'edge';
    /** 来源实体对象 ID。 */
    sourceObjectId: string;
    /** 权威边拓扑索引。 */
    topologyIndex: number;
    /** 运行时边持久化引用。 */
    persistentRef: string;
    /** 运行时边权威子拓扑 ID。 */
    subEntityId?: string;
    /** 相邻面持久化引用。 */
    adjacentFaceRefs?: string[];
    /** 相邻面提示。 */
    adjacentFaceHints?: BlendEdgeTopologyFaceHint[];
    /** 形体哈希。 */
    shapeHash?: number;
}

/**
 * 边选择输入。
 */
export interface BlendEdgeTopologySelection {
    /** 选择项权威子拓扑 ID。 */
    subEntityId?: string;
    /**
     * 语义边名（拓扑命名）。
     *
     * 形如 `edge:<面语义名A>+<面语义名B>`，锚定"由哪些生成元产生"，
     * 既不含坐标也不含枚举下标，因此跨重开与跨上游尺寸变更都保持不变。
     * 旧命令没有该字段，此时退回坐标引用兼容路径。
     */
    semanticEdgeName?: string | null;
    /** 选择持久化引用。 */
    persistentRef: string;
    /** 选择相邻面持久化引用。 */
    adjacentFaceRefs?: string[];
    /** 运行时拓扑提示。 */
    runtimeTopologyHint?: BlendEdgeTopologyRuntimeHint | null;
}

/**
 * 边拓扑候选快照。
 */
export interface BlendEdgeTopologyCandidate {
    /** 边拓扑索引。 */
    edgeIndex: number;
    /**
     * 当前重建产出的语义边名；出处表未提供语义命名时为空。
     * 只有候选侧也具备语义名时，语义阶段才具备权威性。
     */
    semanticEdgeName?: string | null;
    /** 出处表携带的边子拓扑 ID；是否具备权威性由 `provenanceIdentityStrength` 决定。 */
    subEntityId?: string | null;
    /**
     * 出处身份强度。
     * `strong` 表示当前几何引用直接命中出处表，`weak` 表示仅按拓扑索引回绑，`null` 表示没有出处身份。
     */
    provenanceIdentityStrength: 'strong' | 'weak' | null;
    /** Viewer 权威拓扑索引。 */
    topologyIndex: number | null;
    /** 几何端点序列化后的边引用。 */
    geometryPersistentRef: string | null;
    /** 来源于权威拓扑出处表的边引用。 */
    provenancePersistentRef: string | null;
    /** 相邻面持久化引用。 */
    adjacentFaceRefs: string[];
    /** 边形体哈希。 */
    shapeHash?: number;
}

/**
 * 匹配输入。
 */
export interface BlendEdgeTopologyMatchInput {
    /** 来源实体对象 ID。 */
    sourceObjectId?: string;
    /** 选择输入。 */
    selection: BlendEdgeTopologySelection;
    /** 候选边快照。 */
    candidates: BlendEdgeTopologyCandidate[];
}

/**
 * 圆角/倒角边拓扑匹配器。
 *
 * 设计说明：
 * 1. 旧链路把“运行时权威提示”“出处表引用”“几何兜底”揉在一个函数里，导致优先级难以验证；
 * 2. 本类把纯匹配策略抽成独立对象，worker 只负责准备候选快照，匹配决策在这里统一收口；
 * 3. 这样既能先写纯单元测试锁死优先级，也能避免 UI 点击语义继续被 Puppy CAD 内核 枚举细节污染。
 */
export class BlendEdgeTopologyMatcher {
    /**
     * 解析目标边索引。
     *
     * @param input 匹配输入。
     * @returns 命中的边索引。
     */
    public resolve(input: BlendEdgeTopologyMatchInput): number {
        /**
         * 坐标引用直接描述当前物理边，不依赖 Puppy CAD 内核 枚举顺序。
         * 一旦命令携带坐标引用，整个候选空间必须先被该引用限定；找不到或无法消歧时直接报错，
         * 禁止继续使用 subEntityId/topologyIndex/runtime hint 跳到另一条边。
         */
        /**
         * 语义边名锚定生成来源，是唯一同时扛得住"跨重开"与"上游尺寸变更"的身份，
         * 因此排在坐标引用之前。命令或候选任一侧缺语义名时才退回坐标兼容路径。
         */
        const semanticMatch = this.resolveSemanticEdgeName(input);
        if (semanticMatch !== null) {
            return semanticMatch;
        }

        if (isTopologyEdgeCoordinateRef(input.selection.persistentRef)) {
            return this.resolveCoordinatePersistentRef(input);
        }

        const authoritativeSubEntityId = (
            input.selection.subEntityId
            ?? input.selection.runtimeTopologyHint?.subEntityId
        );
        const strongSubEntityId = this.resolveStrongSubEntityId(
            authoritativeSubEntityId,
            input.selection,
        );
        const exactSubEntityMatches = this.filterBySubEntityId(
            input.candidates,
            strongSubEntityId,
        );
        const verifiedStrongSubEntityMatches = this.filterVerifiedStrongSubEntityMatches(
            exactSubEntityMatches,
            input.selection,
        );
        if (verifiedStrongSubEntityMatches.length === 1) {
            return verifiedStrongSubEntityMatches[0]!.edgeIndex;
        }
        if (verifiedStrongSubEntityMatches.length > 1) {
            throw new Error(`边权威拓扑身份匹配存在歧义: ${strongSubEntityId}`);
        }
        this.assertAuthoritativeSubEntityIsResolvable(
            input.candidates,
            strongSubEntityId,
            exactSubEntityMatches.length,
        );

        const runtimeMatch = this.resolveRuntimeHintMatch(input);
        if (runtimeMatch !== null) {
            return runtimeMatch;
        }

        const exactGeometryMatches = input.candidates.filter((candidate) => (
            candidate.geometryPersistentRef === input.selection.persistentRef
        ));
        if (exactGeometryMatches.length === 1) {
            return exactGeometryMatches[0]!.edgeIndex;
        }

        const adjacentFaceMatched = this.filterByAdjacentFaces(
            exactGeometryMatches,
            this.resolveExpectedAdjacentFaceRefs(input.selection),
        );
        if (adjacentFaceMatched.length === 1) {
            return adjacentFaceMatched[0]!.edgeIndex;
        }

        const exactProvenanceMatches = input.candidates.filter((candidate) => (
            candidate.provenanceIdentityStrength === 'strong'
            && candidate.provenancePersistentRef === input.selection.persistentRef
        ));
        if (exactProvenanceMatches.length === 1) {
            return exactProvenanceMatches[0]!.edgeIndex;
        }

        if (exactProvenanceMatches.length > 1 || exactGeometryMatches.length > 1) {
            throw new Error(`边拓扑引用匹配存在歧义: ${input.selection.persistentRef}`);
        }

        throw new Error(`未找到边拓扑引用: ${input.selection.persistentRef}`);
    }

    /**
     * 在坐标引用限定的候选集合内解析目标边。
     *
     * 设计说明：
     * 1. 先按 `geometryPersistentRef` 过滤，确保任何后续消歧都不能越过物理边坐标边界；
     * 2. 重合/seam 边可继续使用同一坐标集合内的相邻面或强子拓扑身份消歧；
     * 3. 零命中与无法消歧均直接抛错，绝不退回旧枚举索引。
     *
     * @param input 匹配输入。
     * @returns 命中的边索引。
     */
    /**
     * 按语义边名解析目标边。
     *
     * 设计说明：
     * 1. 语义名由生成来源派生，上游改尺寸后坐标全变、语义名不变，因此必须排在坐标之前；
     * 2. 只有命令与候选**双方**都具备语义名时该阶段才有权威性；
     *    候选侧没有语义名说明结果体来自尚未接入语义命名的旧出处表，此时必须退回坐标兼容路径，
     *    否则会把"还没升级的旧数据"直接判死；
     * 3. 双方都有语义名却匹配不上，属于真实的身份丢失，必须报错而不是继续降级到坐标或索引——
     *    坐标在该场景下同样是错的，继续降级只会重新引入"静默选错边"。
     *
     * @param input 匹配输入。
     * @returns 命中的边索引；本阶段不适用时返回 `null` 交由后续阶段处理。
     */
    private resolveSemanticEdgeName(input: BlendEdgeTopologyMatchInput): number | null {
        const requestedSemanticName = input.selection.semanticEdgeName?.trim() ?? '';
        if (!requestedSemanticName) {
            return null;
        }
        const semanticCandidates = input.candidates.filter((candidate) => (
            (candidate.semanticEdgeName?.trim() ?? '').length > 0
        ));
        if (semanticCandidates.length === 0) {
            return null;
        }

        const matches = semanticCandidates.filter((candidate) => (
            candidate.semanticEdgeName?.trim() === requestedSemanticName
        ));
        if (matches.length === 1) {
            return matches[0]!.edgeIndex;
        }
        if (matches.length === 0) {
            throw new Error(`未找到语义边名: ${requestedSemanticName}`);
        }

        const adjacentFaceMatches = this.filterByAdjacentFaces(
            matches,
            this.resolveExpectedAdjacentFaceRefs(input.selection),
        );
        if (adjacentFaceMatches.length === 1) {
            return adjacentFaceMatches[0]!.edgeIndex;
        }
        throw new Error(`语义边名匹配存在歧义: ${requestedSemanticName}`);
    }

    private resolveCoordinatePersistentRef(input: BlendEdgeTopologyMatchInput): number {
        const requestedPersistentRef = input.selection.persistentRef;
        const coordinateMatches = input.candidates.filter((candidate) => (
            candidate.geometryPersistentRef === requestedPersistentRef
        ));
        if (coordinateMatches.length === 0) {
            throw new Error(`未找到边拓扑引用: ${requestedPersistentRef}`);
        }
        if (coordinateMatches.length === 1) {
            return coordinateMatches[0]!.edgeIndex;
        }

        const adjacentFaceMatches = this.filterByAdjacentFaces(
            coordinateMatches,
            this.resolveCoordinateAdjacentFaceRefs(input.selection),
        );
        if (adjacentFaceMatches.length === 1) {
            return adjacentFaceMatches[0]!.edgeIndex;
        }

        const strongSubEntityId = this.resolveStrongSubEntityId(
            input.selection.subEntityId ?? input.selection.runtimeTopologyHint?.subEntityId,
            input.selection,
        );
        const strongIdentityMatches = this.filterBySubEntityId(
            adjacentFaceMatches.length > 0 ? adjacentFaceMatches : coordinateMatches,
            strongSubEntityId,
        ).filter((candidate) => candidate.provenanceIdentityStrength === 'strong');
        if (strongIdentityMatches.length === 1) {
            return strongIdentityMatches[0]!.edgeIndex;
        }

        throw new Error(`边拓扑引用匹配存在歧义: ${requestedPersistentRef}`);
    }

    /**
     * 解析坐标候选消歧可使用的相邻面引用。
     *
     * 只有 runtime hint 自身引用与命令坐标引用一致时，才允许其相邻面参与；
     * 这样可避免 stale runtime hint 再次把坐标候选导向旧索引身份。
     *
     * @param selection 当前选择输入。
     * @returns 可用于坐标候选消歧的相邻面集合。
     */
    private resolveCoordinateAdjacentFaceRefs(
        selection: BlendEdgeTopologySelection,
    ): string[] {
        const expectedRefs = new Set(selection.adjacentFaceRefs ?? []);
        const runtimeHint = selection.runtimeTopologyHint;
        if (runtimeHint?.persistentRef === selection.persistentRef) {
            (runtimeHint.adjacentFaceRefs ?? []).forEach((faceRef) => expectedRefs.add(faceRef));
            (runtimeHint.adjacentFaceHints ?? []).forEach((faceHint) => {
                if (faceHint.persistentRef) {
                    expectedRefs.add(faceHint.persistentRef);
                }
            });
        }
        return [...expectedRefs].filter((faceRef) => faceRef.length > 0);
    }

    /**
     * 解析运行时提示匹配。
     *
     * @param input 匹配输入。
     * @returns 命中的边索引；无法命中时返回 `null`。
     */
    private resolveRuntimeHintMatch(input: BlendEdgeTopologyMatchInput): number | null {
        const runtimeHint = input.selection.runtimeTopologyHint;
        if (!runtimeHint || runtimeHint.kind !== 'edge') {
            return null;
        }
        if (
            typeof input.sourceObjectId === 'string'
            && runtimeHint.sourceObjectId !== input.sourceObjectId
        ) {
            return null;
        }

        const exactSubEntityMatches = this.filterBySubEntityId(input.candidates, runtimeHint.subEntityId);
        const verifiedStrongSubEntityMatches = this.filterVerifiedStrongSubEntityMatches(
            exactSubEntityMatches,
            input.selection,
        );
        if (verifiedStrongSubEntityMatches.length === 1) {
            return verifiedStrongSubEntityMatches[0]!.edgeIndex;
        }

        /**
         * 运行时提示里最稳定的线索仍然是边自身的稳定身份。
         *
         * 设计说明：
         * 1. 经当前几何自证的 `subEntityId` 是当前已加载模型中的权威主键；
         * 2. `persistentRef` 负责跨会话与跨重建回到同一个拓扑对象；
         * 3. 相邻面提示只允许作为歧义消解条件，不能抢走已经唯一命中的目标边。
         */
        const exactPersistentRefMatched = this.filterByPersistentRef(
            input.candidates,
            runtimeHint.persistentRef,
        );
        if (exactPersistentRefMatched.length === 1) {
            return exactPersistentRefMatched[0]!.edgeIndex;
        }

        const topologyIndexMatch = this.resolveTopologyIndexMatch(input.candidates, runtimeHint, input.selection);
        if (topologyIndexMatch !== null) {
            return topologyIndexMatch;
        }

        const hintedCandidates = this.filterByRuntimeHint(
            exactPersistentRefMatched.length > 0 ? exactPersistentRefMatched : input.candidates,
            runtimeHint,
        );
        if (hintedCandidates.length === 1) {
            return hintedCandidates[0]!.edgeIndex;
        }

        const adjacentFaceMatched = this.filterByAdjacentFaces(
            hintedCandidates,
            this.resolveExpectedAdjacentFaceRefs(input.selection),
        );
        if (adjacentFaceMatched.length === 1) {
            return adjacentFaceMatched[0]!.edgeIndex;
        }

        return null;
    }

    /**
     * 在同源前提下，尝试使用 runtime topologyIndex 做受控回绑。
     *
     * 设计说明：
     * 1. topologyIndex 绝不能无条件当真相，它只允许在“同一 sourceObjectId”且其它线索不否定它时参与回绑；
     * 2. 这里至少要求满足以下任一条件：边 shapeHash 一致、相邻面集合能够自证、边自身 persistentRef 能命中；
     * 3. 历史非坐标引用允许在 `subEntityId + provenance ref + topologyIndex` 全部一致时兼容回绑；
     * 4. 坐标引用绝不进入该兼容分支，保证跨重建时当前几何身份始终高于旧索引身份。
     *
     * @param candidates 候选集合。
     * @param runtimeHint 运行时边提示。
     * @param selection 当前选择输入。
     * @returns 命中的边索引；无法证明同源时返回 `null`。
     */
    private resolveTopologyIndexMatch(
        candidates: BlendEdgeTopologyCandidate[],
        runtimeHint: BlendEdgeTopologyRuntimeHint,
        selection: BlendEdgeTopologySelection,
    ): number | null {
        if (!Number.isInteger(runtimeHint.topologyIndex)) {
            return null;
        }

        const hintedCandidate = candidates.find((candidate) => candidate.topologyIndex === runtimeHint.topologyIndex) ?? null;
        if (!hintedCandidate) {
            return null;
        }

        const persistentRefMatched = this.filterByPersistentRef([hintedCandidate], runtimeHint.persistentRef).length === 1;
        if (persistentRefMatched) {
            return hintedCandidate.edgeIndex;
        }

        const expectedFaceRefs = this.resolveExpectedAdjacentFaceRefs(selection);
        const adjacentFaceMatched = this.filterByAdjacentFaces([hintedCandidate], expectedFaceRefs).length === 1;
        if (adjacentFaceMatched) {
            return hintedCandidate.edgeIndex;
        }

        if (
            Number.isInteger(runtimeHint.shapeHash)
            && hintedCandidate.shapeHash === runtimeHint.shapeHash
        ) {
            return hintedCandidate.edgeIndex;
        }

        /**
         * 旧版本可能保存 `authority-edge:*` 这类非坐标持久化引用，无法与当前端点 ref 直接比较。
         * 此时仅在同源 runtime hint 已通过外层校验，且 ID、出处 ref、索引三项同时一致时保留兼容回绑。
         * 对坐标引用禁止走此路径，避免 Puppy CAD 内核 枚举顺序变化后把旧索引身份错误绑定到另一条边。
         */
        if (
            !isTopologyEdgeCoordinateRef(runtimeHint.persistentRef)
            && runtimeHint.persistentRef === selection.persistentRef
            && hintedCandidate.provenanceIdentityStrength === 'weak'
            && hintedCandidate.subEntityId === runtimeHint.subEntityId
            && hintedCandidate.provenancePersistentRef === runtimeHint.persistentRef
        ) {
            return hintedCandidate.edgeIndex;
        }

        return null;
    }

    /**
     * 按运行时提示过滤候选。
     *
     * @param candidates 候选集合。
     * @param runtimeHint 运行时提示。
     * @returns 过滤后的候选集合。
     */
    private filterByRuntimeHint(
        candidates: BlendEdgeTopologyCandidate[],
        runtimeHint: BlendEdgeTopologyRuntimeHint,
    ): BlendEdgeTopologyCandidate[] {
        const persistentRefMatched = this.filterByPersistentRef(candidates, runtimeHint.persistentRef);
        if (persistentRefMatched.length > 0) {
            const adjacentFaceMatched = this.filterByAdjacentFaces(
                persistentRefMatched,
                this.resolveExpectedAdjacentFaceRefs({
                    persistentRef: runtimeHint.persistentRef,
                    adjacentFaceRefs: runtimeHint.adjacentFaceRefs,
                    runtimeTopologyHint: runtimeHint,
                }),
            );
            if (adjacentFaceMatched.length > 0) {
                return adjacentFaceMatched;
            }
            return persistentRefMatched;
        }

        const expectedFaceRefs = this.resolveExpectedAdjacentFaceRefs({
            persistentRef: runtimeHint.persistentRef,
            adjacentFaceRefs: runtimeHint.adjacentFaceRefs,
            runtimeTopologyHint: runtimeHint,
        });
        const faceMatched = this.filterByAdjacentFaces(candidates, expectedFaceRefs);
        if (faceMatched.length > 0) {
            return faceMatched;
        }

        if (Number.isInteger(runtimeHint.shapeHash)) {
            const hashedCandidates = candidates.filter((candidate) => (
                candidate.shapeHash === runtimeHint.shapeHash
            ));
            if (hashedCandidates.length > 0) {
                return hashedCandidates;
            }
        }

        return this.filterByPersistentRef(candidates, runtimeHint.persistentRef);
    }

    /**
     * 按边自身持久化引用过滤候选。
     *
     * @param candidates 候选集合。
     * @param persistentRef 目标持久化引用。
     * @returns 命中的候选集合。
     */
    private filterByPersistentRef(
        candidates: BlendEdgeTopologyCandidate[],
        persistentRef: string,
    ): BlendEdgeTopologyCandidate[] {
        const geometryMatches = candidates.filter((candidate) => (
            candidate.geometryPersistentRef === persistentRef
        ));
        if (geometryMatches.length > 0) {
            return geometryMatches;
        }
        return candidates.filter((candidate) => (
            candidate.provenanceIdentityStrength === 'strong'
            && candidate.provenancePersistentRef === persistentRef
        ));
    }

    /**
     * 按权威子拓扑 ID 过滤候选。
     *
     * @param candidates 候选集合。
     * @param subEntityId 目标权威子拓扑 ID。
     * @returns 命中的候选集合。
     */
    private filterBySubEntityId(
        candidates: BlendEdgeTopologyCandidate[],
        subEntityId: string | null | undefined,
    ): BlendEdgeTopologyCandidate[] {
        if (typeof subEntityId !== 'string' || subEntityId.length <= 0) {
            return [];
        }
        return candidates.filter((candidate) => candidate.subEntityId === subEntityId);
    }

    /**
     * 过滤经过当前几何与相邻面证据交叉验证的强身份候选。
     *
     * 设计说明：
     * 1. 只有当前几何 ref 直接命中出处表的候选才具备强身份；
     * 2. 即使 `subEntityId` 相同，也必须与选择保存的坐标 ref 一致；
     * 3. 选择携带相邻面时必须同时一致，防止重复端点或 seam 边误命中。
     *
     * @param candidates 已按子拓扑 ID 命中的候选集合。
     * @param selection 当前选择输入。
     * @returns 能够由当前几何证据自证的强身份候选。
     */
    private filterVerifiedStrongSubEntityMatches(
        candidates: BlendEdgeTopologyCandidate[],
        selection: BlendEdgeTopologySelection,
    ): BlendEdgeTopologyCandidate[] {
        const expectedFaceRefs = this.resolveExpectedAdjacentFaceRefs(selection);
        return candidates.filter((candidate) => {
            if (candidate.provenanceIdentityStrength !== 'strong') {
                return false;
            }
            if (candidate.geometryPersistentRef !== selection.persistentRef) {
                return false;
            }
            return expectedFaceRefs.length === 0
                || this.hasAllAdjacentFaces(candidate, expectedFaceRefs);
        });
    }

    /**
     * 解析应当作为强权威约束的子拓扑 ID。
     *
     * 设计说明：
     * 1. `subEntityId` 有两类来源：模型出处表里的权威 ID，以及 Viewer 在缺少出处表时从几何 ref 派生的兼容 ID；
     * 2. 兼容 ID 与 `persistentRef` 本质相同，不能阻止后续 persistentRef / runtime hint 回退匹配；
     * 3. 出处表 ID 才是强身份。它一旦存在但命不中，就必须抛错，防止又把相似端点边误认为用户选择。
     *
     * @param subEntityId 选择携带的子拓扑 ID。
     * @param selection 选择输入。
     * @returns 强权威 ID；几何派生 ID 返回 `null`。
     */
    private resolveStrongSubEntityId(
        subEntityId: string | null | undefined,
        selection: BlendEdgeTopologySelection,
    ): string | null {
        if (typeof subEntityId !== 'string' || subEntityId.length <= 0) {
            return null;
        }
        const persistentRefs = [
            selection.persistentRef,
            selection.runtimeTopologyHint?.persistentRef,
        ].filter((persistentRef): persistentRef is string => (
            typeof persistentRef === 'string' && persistentRef.length > 0
        ));
        const weakSubEntityIds = persistentRefs.flatMap((persistentRef) => [
            persistentRef,
            buildTopologySubEntityId('edge', persistentRef),
        ]);
        if (weakSubEntityIds.includes(subEntityId)) {
            return null;
        }
        return subEntityId;
    }

    /**
     * 断言权威子拓扑身份可以被当前候选明确命中。
     *
     * @param candidates 边候选集合。
     * @param subEntityId 选择请求携带的权威子拓扑 ID。
     * @param matchedCount 精确命中的候选数量。
     */
    private assertAuthoritativeSubEntityIsResolvable(
        candidates: BlendEdgeTopologyCandidate[],
        subEntityId: string | null | undefined,
        matchedCount: number,
    ): void {
        if (typeof subEntityId !== 'string' || subEntityId.length <= 0) {
            return;
        }
        if (matchedCount > 0) {
            return;
        }

        const hasAuthoritativeCandidateIdentity = candidates.some((candidate) => (
            candidate.provenanceIdentityStrength === 'strong'
            && typeof candidate.subEntityId === 'string'
            && candidate.subEntityId.length > 0
        ));

        /**
         * 只有当前候选不存在可由几何 ref 证明的强身份时才允许兼容回退。
         * 索引回绑的弱身份不能阻止坐标几何匹配；真正的强身份缺失时仍维持原有严格报错。
         */
        if (hasAuthoritativeCandidateIdentity) {
            throw new Error(`未找到权威边拓扑身份: ${subEntityId}`);
        }
    }

    /**
     * 解析期望的相邻面引用集合。
     *
     * @param selection 选择输入。
     * @returns 去重后的相邻面引用集合。
     */
    private resolveExpectedAdjacentFaceRefs(
        selection: BlendEdgeTopologySelection,
    ): string[] {
        const expectedRefs = new Set<string>();
        (selection.adjacentFaceRefs ?? []).forEach((faceRef) => {
            if (faceRef) {
                expectedRefs.add(faceRef);
            }
        });
        (selection.runtimeTopologyHint?.adjacentFaceRefs ?? []).forEach((faceRef) => {
            if (faceRef) {
                expectedRefs.add(faceRef);
            }
        });
        (selection.runtimeTopologyHint?.adjacentFaceHints ?? []).forEach((faceHint) => {
            if (faceHint.persistentRef) {
                expectedRefs.add(faceHint.persistentRef);
            }
        });
        return [...expectedRefs];
    }

    /**
     * 按相邻面过滤候选。
     *
     * @param candidates 候选集合。
     * @param expectedFaceRefs 期望的相邻面引用集合。
     * @returns 命中的候选集合。
     */
    private filterByAdjacentFaces(
        candidates: BlendEdgeTopologyCandidate[],
        expectedFaceRefs: string[],
    ): BlendEdgeTopologyCandidate[] {
        if (expectedFaceRefs.length === 0) {
            return [];
        }
        return candidates.filter((candidate) => this.hasAllAdjacentFaces(candidate, expectedFaceRefs));
    }

    /**
     * 判断候选是否包含全部期望相邻面。
     *
     * @param candidate 候选快照。
     * @param expectedFaceRefs 期望相邻面集合。
     * @returns 是否全部包含。
     */
    private hasAllAdjacentFaces(
        candidate: BlendEdgeTopologyCandidate,
        expectedFaceRefs: string[],
    ): boolean {
        if (expectedFaceRefs.length === 0) {
            return false;
        }
        const candidateFaceRefs = new Set(candidate.adjacentFaceRefs);
        return expectedFaceRefs.every((faceRef) => candidateFaceRefs.has(faceRef));
    }
}
